import { Link } from "react-router-dom";
import {
  BookOpen,
  Award,
  Users2,
  HandCoins,
  Briefcase,
  ShieldCheck,
  FileText,
  MessageCircle,
  Sparkles,
  ArrowRight,
  Download,
} from "lucide-react";
import { PolicyPageShell } from "@/components/PolicyHeader";
import { API } from "@/lib/api";

const ADVISOR_MODULES = [
  { icon: BookOpen, t: "Company Introduction", d: "Vision, mission, business model & corporate structure of Sahyog Finance and Noble Infinity." },
  { icon: HandCoins, t: "Product Knowledge", d: "All 14 loan products plus Mutual Funds, SIPs, Shares & Project Finance — features, eligibility, rates." },
  { icon: Users2, t: "Customer Service Standards", d: "Professional client interaction protocols, expectations management & after-sales care." },
  { icon: Sparkles, t: "Lead Generation SOP", d: "Field activities, referral systems, digital channels and the 80/20 conversion playbook." },
  { icon: FileText, t: "Documentation Standards", d: "KYC, application forms, audit trail and how to prepare an underwriting-ready file." },
  { icon: ShieldCheck, t: "Compliance & Ethics", d: "RBI guidelines, mis-selling prevention, code of conduct and anti-corruption practices." },
  { icon: Award, t: "Data Privacy & Confidentiality", d: "Customer data protection, IT security, and the consequences of unauthorised disclosure." },
];

const DSA_MODULES = [
  { icon: BookOpen, t: "Company Policies", d: "Onboarding terms, payout structures, and territorial assignments." },
  { icon: HandCoins, t: "Product Knowledge", d: "Detailed walkthrough of every loan product and asset-management offering." },
  { icon: Users2, t: "Customer Acquisition", d: "Lead-to-customer conversion techniques and pipeline discipline." },
  { icon: FileText, t: "Documentation Standards", d: "Submission checklists, supporting documents, and quality assurance." },
  { icon: ShieldCheck, t: "Compliance Requirements", d: "DSA Code of Conduct, mandatory disclosures and regulatory filings." },
  { icon: Award, t: "Data Protection", d: "Information security, secure data handling and customer privacy." },
  { icon: Sparkles, t: "Customer Service Excellence", d: "Communication etiquette, complaint handling and relationship retention." },
  { icon: Briefcase, t: "Ethical Business Practices", d: "Transparency, fair dealings and our zero-tolerance policy on misrepresentation." },
];

export default function Training() {
  return (
    <PolicyPageShell
      testId="training-page"
      eyebrow="Learning & Development"
      title={
        <>
          Training, <em className="not-italic text-[#D4AF37]">Certification</em> &amp;{" "}
          <span className="font-script italic text-[#F0D788]">Growth</span>
        </>
      }
      lead="Every Advisor and DSA who joins Sahyog goes through a structured training pack — equipping you with product mastery, compliance know-how and customer service excellence before you take the field."
    >
      {/* Cert badges strip */}
      <div className="grid sm:grid-cols-3 gap-4 mb-14">
        <Badge title="Advisor Certification" desc="Validates completion of all 7 mandatory modules" />
        <Badge title="DSA Completion Certificate" desc="Issued upon clearing the DSA Business Development & Compliance Program" />
        <Badge title="Trainer-led + Self-paced" desc="Classroom, field and digital modules combined" />
      </div>

      {/* Advisor track */}
      <section className="mb-16">
        <div className="flex items-end justify-between flex-wrap gap-3 mb-7">
          <div>
            <div className="text-[11px] tracking-[0.3em] uppercase text-[#D4AF37]">Track A</div>
            <h2 className="font-display text-3xl mt-2">Financial Advisor Training</h2>
            <p className="mt-1 text-white/60 text-sm">7 mandatory modules · Certification on completion</p>
          </div>
          <Link to="/apply/advisor" data-testid="training-cta-advisor" className="px-5 py-2.5 rounded-full btn-gold text-sm font-semibold inline-flex items-center gap-2">
            Enrol as Advisor <ArrowRight size={14} />
          </Link>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {ADVISOR_MODULES.map((m, i) => (
            <ModuleCard key={m.t} {...m} idx={i + 1} testId={`training-advisor-${i + 1}`} />
          ))}
        </div>
      </section>

      {/* DSA track */}
      <section className="mb-16">
        <div className="flex items-end justify-between flex-wrap gap-3 mb-7">
          <div>
            <div className="text-[11px] tracking-[0.3em] uppercase text-[#D4AF37]">Track B</div>
            <h2 className="font-display text-3xl mt-2">DSA Business Development &amp; Compliance Program</h2>
            <p className="mt-1 text-white/60 text-sm">8 modules · Trainer signature + Branch Manager + HR sign-off</p>
          </div>
          <Link to="/apply/dsa" data-testid="training-cta-dsa" className="px-5 py-2.5 rounded-full btn-outline-gold text-sm font-semibold inline-flex items-center gap-2">
            Enrol as DSA <ArrowRight size={14} />
          </Link>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {DSA_MODULES.map((m, i) => (
            <ModuleCard key={m.t} {...m} idx={i + 1} testId={`training-dsa-${i + 1}`} compact />
          ))}
        </div>
      </section>

      {/* Evaluation */}
      <section className="rounded-3xl bg-white/[0.04] border border-[#D4AF37]/25 p-7 md:p-10">
        <h2 className="font-display text-2xl">Evaluation &amp; Certification</h2>
        <div className="mt-6 grid md:grid-cols-3 gap-5 text-sm">
          <div>
            <div className="text-[10px] tracking-[0.28em] uppercase text-[#D4AF37]">Step 1</div>
            <div className="font-display text-lg mt-2 text-white">Attendance &amp; Participation</div>
            <p className="mt-1 text-white/60">Recorded via the Attendance Register (Sr No · Name · ID · Designation · Mobile · Signature).</p>
          </div>
          <div>
            <div className="text-[10px] tracking-[0.28em] uppercase text-[#D4AF37]">Step 2</div>
            <div className="font-display text-lg mt-2 text-white">Assessment (1 – 5 scale)</div>
            <p className="mt-1 text-white/60">Knowledge, skills and compliance scored by the trainer. Reassessment scheduled if needed.</p>
          </div>
          <div>
            <div className="text-[10px] tracking-[0.28em] uppercase text-[#D4AF37]">Step 3</div>
            <div className="font-display text-lg mt-2 text-white">Certificate Issued</div>
            <p className="mt-1 text-white/60">Authorised by Trainer · Branch Manager · HR. Required before any business activity.</p>
          </div>
        </div>
        <div className="mt-8 flex flex-wrap gap-3">
          <Link to="/apply/advisor" data-testid="training-bottom-advisor" className="px-6 py-3 rounded-full btn-gold text-sm font-semibold">
            Apply as Advisor
          </Link>
          <Link to="/apply/dsa" data-testid="training-bottom-dsa" className="px-6 py-3 rounded-full btn-outline-gold text-sm font-semibold">
            Apply as DSA
          </Link>
          <a href="https://wa.me/917016975324" target="_blank" rel="noreferrer" className="px-6 py-3 rounded-full border border-emerald-500/30 bg-emerald-600/15 hover:bg-emerald-600/25 text-emerald-200 text-sm font-semibold inline-flex items-center gap-2">
            <MessageCircle size={14} /> Ask Training Cell
          </a>
        </div>
      </section>

      {/* Training Forms — Learning & Development Pack */}
      <section data-testid="training-forms" className="mt-16 md:mt-20">
        <div className="text-[11px] uppercase tracking-[0.3em] text-[#D4AF37] mb-3">
          Ultra Professional Learning &amp; Development (L&amp;D) Pack
        </div>
        <h2 className="font-serif text-2xl md:text-3xl text-white max-w-3xl leading-tight">
          Standard training forms &amp; certification documents
        </h2>
        <p className="mt-4 text-sm md:text-base text-white/70 max-w-3xl leading-relaxed">
          Four official internal forms govern our training operations — used by every Branch
          Manager, Trainer and HR Officer across Sahyog Finance &amp; Noble Infinity.
        </p>

        <div className="mt-8 grid md:grid-cols-2 gap-5">
          {[
            {
              code: "SF-NI-LD-TAS-001",
              title: "Training Attendance Sheet",
              part: "Part A",
              body: "Captures program details (name, date, venue, trainer, department, duration) plus an attendance register for up to 10 participants with ID code, designation, mobile and signature.",
            },
            {
              code: "SF-NI-LD-ETF-001",
              title: "Employee Training Evaluation Form",
              part: "Part B",
              body: "Rates employees on 8 parameters (1–5 scale): Subject Knowledge · Communication · Product Understanding · Compliance Awareness · Customer Handling · Practical Application · Participation · Overall Performance. Includes trainer comments and HR sign-off.",
            },
            {
              code: "SF-NI-LD-ATC-001",
              title: "Advisor Training Certification Form",
              part: "Part C",
              body: "Tracks completion of 7 mandatory modules: Company Introduction · Product Knowledge · Customer Service Standards · Lead Generation SOP · Documentation Standards · Compliance & Ethics · Data Privacy & Confidentiality.",
            },
            {
              code: "SF-NI-LD-DTC-001",
              title: "DSA Training Completion Certificate",
              part: "Part D",
              body: "Official certificate covering 8 areas (Company Policies, Product Knowledge, Customer Acquisition, Documentation Standards, Compliance, Data Protection, Customer Service Excellence, Ethical Business Practices) with Trainer + Branch Manager + HR signatures and company seal.",
            },
          ].map((f, i) => (
            <div
              key={f.code}
              data-testid={`training-form-${i}`}
              className="rounded-2xl border border-[#D4AF37]/15 bg-[#0A1F44] p-6 hover:border-[#D4AF37]/45 transition flex flex-col"
            >
              <div className="flex items-center justify-between mb-3">
                <span className="text-[10px] uppercase tracking-[0.25em] text-[#F0D788] bg-[#D4AF37]/10 px-2.5 py-1 rounded-full border border-[#D4AF37]/30">
                  {f.part}
                </span>
                <span className="text-[10px] font-mono text-white/50">{f.code}</span>
              </div>
              <h3 className="font-serif text-lg text-white mb-2">{f.title}</h3>
              <p className="text-sm text-white/70 leading-relaxed">{f.body}</p>
              <a
                data-testid={`training-form-download-${f.code}`}
                href={`${API}/training/forms/${f.code}/pdf`}
                target="_blank"
                rel="noreferrer"
                className="mt-5 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full border border-[#D4AF37]/50 bg-[#D4AF37]/10 text-[#F0D788] hover:bg-[#D4AF37]/20 hover:border-[#D4AF37] text-xs font-semibold uppercase tracking-[0.15em] transition self-start"
              >
                <Download size={13} /> Download blank PDF
              </a>
            </div>
          ))}
        </div>

        {/* Training motto + tagline */}
        <div className="mt-10 grid md:grid-cols-2 gap-5">
          <div className="rounded-2xl border border-[#D4AF37]/25 bg-gradient-to-br from-[#0A1F44] to-[#04102B] p-6">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] mb-2">
              Corporate Motto
            </div>
            <div className="font-serif text-lg md:text-xl text-white leading-snug">
              Learn Continuously ·{" "}
              <span className="text-[#F0D788]">Perform Professionally</span> · Lead Successfully.
            </div>
          </div>
          <div className="rounded-2xl border border-[#D4AF37]/25 bg-gradient-to-br from-[#0A1F44] to-[#04102B] p-6">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] mb-2">
              Corporate Tagline
            </div>
            <div className="font-serif text-lg md:text-xl text-white leading-snug">
              Training Today.{" "}
              <span className="font-script italic text-[#F0D788]">Leadership Tomorrow.</span>
            </div>
          </div>
        </div>

        {/* Document control footer */}
        <div className="mt-8 rounded-xl border border-white/10 bg-white/[0.02] px-5 py-4 text-xs text-white/60 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div>
            <span className="text-white/65 uppercase tracking-wider text-[10px] block">Form series</span>
            <span className="text-white/85">Learning & Development Forms</span>
          </div>
          <div>
            <span className="text-white/65 uppercase tracking-wider text-[10px] block">Version</span>
            <span className="text-white/85">1.0</span>
          </div>
          <div>
            <span className="text-white/65 uppercase tracking-wider text-[10px] block">Effective date</span>
            <span className="text-white/85">01 January 2026</span>
          </div>
          <div>
            <span className="text-white/65 uppercase tracking-wider text-[10px] block">Retention</span>
            <span className="text-white/85">10 years · Internal records</span>
          </div>
        </div>
      </section>
    </PolicyPageShell>
  );
}

function ModuleCard({ icon: I, t, d, idx, testId, compact }) {
  return (
    <div
      data-testid={testId}
      className="relative rounded-2xl p-5 border border-white/10 bg-white/[0.03] hover:border-[#D4AF37]/40 transition-all"
    >
      <span className="absolute top-4 right-4 text-[10px] text-white/30 tracking-wider">
        {String(idx).padStart(2, "0")}
      </span>
      <I size={22} className="text-[#F0D788]" />
      <div className={`mt-3 font-display ${compact ? "text-base" : "text-lg"} text-white`}>{t}</div>
      <p className="mt-1.5 text-white/55 text-xs leading-relaxed">{d}</p>
    </div>
  );
}

function Badge({ title, desc }) {
  return (
    <div className="rounded-2xl p-5 border border-[#D4AF37]/30 bg-[#D4AF37]/[0.06] flex items-start gap-3">
      <Award size={22} className="text-[#F0D788] shrink-0" />
      <div>
        <div className="font-display text-base text-white">{title}</div>
        <p className="text-white/60 text-xs mt-1">{desc}</p>
      </div>
    </div>
  );
}
